import React, { useEffect, useMemo, useRef, useState } from "react";
import { FileText, Loader2 } from "lucide-react";
import { Document, Page, pdfjs } from "react-pdf";

// Canvas rendering via PDF.js — works on mobile browsers that refuse to embed PDFs.
pdfjs.GlobalWorkerOptions.workerSrc =
  `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export default function ScannedPdfPreview({ url, testid = "scanned-pdf-preview" }) {
  const boxRef = useRef(null);
  const [width, setWidth] = useState(0);
  const [pages, setPages] = useState(0);
  const [failed, setFailed] = useState(false);
  // Stable object: a new `file` object on every parent re-render makes react-pdf reload (flicker).
  const file = useMemo(() => ({ url, withCredentials: true }), [url]);

  useEffect(() => {
    const el = boxRef.current;
    if (!el) return undefined;
    // Measure the non-scrolling wrapper and ignore tiny changes: measuring the
    // scroll box itself made its scrollbar toggle the width → endless re-render loop.
    const measure = () => {
      const w = Math.floor(el.clientWidth) - 14;
      setWidth((prev) => (Math.abs(prev - w) > 8 ? w : prev));
    };
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    measure();
    return () => ro.disconnect();
  }, []);

  if (failed) {
    return (
      <a href={url} target="_blank" rel="noreferrer" data-testid={`${testid}-fallback`}
        className="w-full h-32 flex items-center justify-center gap-2 text-sm text-blue-700 hover:underline">
        <FileText size={16} /> Ouvrir le PDF scanné
      </a>
    );
  }
  return (
    <div ref={boxRef} className="w-full" data-testid={testid}>
    <div className="max-h-80 overflow-y-auto rounded bg-white" style={{ scrollbarGutter: "stable" }} data-testid={`${testid}-scroll`}>
      <Document
        file={file}
        onLoadSuccess={({ numPages }) => setPages(numPages)}
        onLoadError={() => setFailed(true)}
        loading={<div className="h-32 flex items-center justify-center text-zinc-400"><Loader2 size={18} className="animate-spin" /></div>}
      >
        {width > 0 && Array.from({ length: pages }, (_, i) => (
          <Page key={i} pageNumber={i + 1} width={width} renderTextLayer={false} renderAnnotationLayer={false}
            loading={<div style={{ height: Math.round(width * 1.414) }} className="bg-zinc-50" />}
            className={i ? "mt-2 border-t border-zinc-100" : ""} />
        ))}
      </Document>
    </div>
      {pages > 1 && <div className="text-[10px] font-mono text-zinc-500 text-center py-1">{pages} pages — faites défiler</div>}
    </div>
  );
}
